/**
 * roastEngine — deterministic "teacher's comments" for the report card.
 *
 * Voice: group-chat spicy. Mean about fantasy decisions, never about people.
 * No profanity. Every roast is tied to a real metric from the API — nothing
 * is fabricated.
 *
 * Determinism: the same team + season always gets the same roast, so shared
 * links and screenshots stay consistent.
 */

import { LetterGrade, gradeBand } from './gradeEngine';

export interface RoastMetrics {
  // Draft (from draft analytics response)
  avgValueScore?: number;
  draftGrade?: LetterGrade;
  worstPickName?: string;
  worstPickRound?: number;
  bestPickName?: string;
  bestPickRound?: number;
  /** Round a K or D/ST was drafted in, if suspiciously early (<= 9). */
  earlyKdstRound?: number;
  earlyKdstName?: string;
  earlyKdstPosition?: string;

  // Season (from standings response)
  wins?: number;
  losses?: number;
  pointsFor?: number;
  pointsAgainst?: number;
  leagueAvgPointsFor?: number;
  rank?: number;
  totalTeams?: number;

  // Activity (from team detail)
  acquisitions?: number;
  trades?: number;

  // Superlative award names this team "won"
  superlativeAwards?: string[];
}

interface RoastRule {
  id: string;
  matches: (m: RoastMetrics) => boolean;
  templates: Array<(m: RoastMetrics) => string>;
}

/** mulberry32 — tiny deterministic PRNG. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const fmt = (n: number | undefined, d = 0): string =>
  n === undefined ? '?' : Math.abs(n).toFixed(d);

const RULES: RoastRule[] = [
  {
    id: 'draft-failed',
    matches: (m) => m.avgValueScore !== undefined && m.avgValueScore < -25,
    templates: [
      (m) =>
        `Graded on a curve. Still failed. Your picks came in ${fmt(m.avgValueScore)} points under round expectations, on average.`,
      () =>
        'Your draft was a masterclass in letting good players go one pick before you.',
    ],
  },
  {
    id: 'early-kdst',
    matches: (m) => m.earlyKdstRound !== undefined && m.earlyKdstRound <= 9,
    templates: [
      (m) =>
        `${m.earlyKdstName ?? 'A kicker'} (${m.earlyKdstPosition ?? 'K'}) in round ${m.earlyKdstRound}. Before round 10. See me after class.`,
      (m) =>
        `Round ${m.earlyKdstRound}: everyone else drafted upside. You drafted ${m.earlyKdstName ?? 'a kicker'}.`,
    ],
  },
  {
    id: 'schedule-victim',
    matches: (m) =>
      m.wins !== undefined &&
      m.losses !== undefined &&
      m.pointsFor !== undefined &&
      m.leagueAvgPointsFor !== undefined &&
      m.wins < m.losses &&
      m.pointsFor > m.leagueAvgPointsFor,
    templates: [
      () => 'Top half in scoring, bottom half in wins. Your real opponent was the schedule. It won.',
      (m) =>
        `${m.losses} losses while scoring above the league average. That's not bad luck — that's the universe leaving a comment.`,
    ],
  },
  {
    id: 'bottom-quartile',
    matches: (m) =>
      m.rank !== undefined && m.totalTeams !== undefined && m.rank > m.totalTeams * 0.75,
    templates: [
      (m) =>
        `${m.rank} of ${m.totalTeams}. That's not a rough season — that's a teaching moment.`,
      (m) =>
        `Finished ${m.rank} out of ${m.totalTeams}. The good news: the draft order likes you now.`,
    ],
  },
  {
    id: 'round-one-regret',
    matches: (m) => m.worstPickRound === 1 && !!m.worstPickName,
    templates: [
      (m) =>
        `${m.worstPickName} in round 1. Some first-round picks build dynasties. This one built character.`,
      (m) => `You spent your first-rounder on ${m.worstPickName}. The league thanks you for your donation.`,
    ],
  },
  {
    id: 'waiver-addict',
    matches: (m) => m.acquisitions !== undefined && m.acquisitions > 20,
    templates: [
      (m) =>
        `${m.acquisitions} pickups. You were on the waiver wire like it owed you money.`,
      (m) => `${m.acquisitions} roster moves. Activity is not the same as progress.`,
    ],
  },
  {
    id: 'unluckiest-loss',
    matches: (m) => (m.superlativeAwards ?? []).includes('Unluckiest Loss'),
    templates: [
      () => 'Scored enough to win. Lost anyway. The football gods reviewed your season and chose violence.',
      () => 'You put up a winning score and still took the L. Frame it — proof the schedule was personal.',
    ],
  },
  {
    id: 'drafted-well-played-badly',
    matches: (m) =>
      m.avgValueScore !== undefined &&
      m.wins !== undefined &&
      m.totalTeams !== undefined &&
      m.avgValueScore > 25 &&
      m.rank !== undefined &&
      m.rank > m.totalTeams / 2,
    templates: [
      () =>
        'Elite draft. Mid-table finish. Somewhere between August and December, the plan unraveled.',
      () => 'You drafted like an analyst and managed like you lost your login in October.',
    ],
  },
  // Positive-leaning — A/B teams get earned respect with a smirk.
  {
    id: 'annoyingly-correct',
    matches: (m) =>
      m.avgValueScore !== undefined &&
      m.rank !== undefined &&
      m.totalTeams !== undefined &&
      m.avgValueScore > 25 &&
      m.rank <= Math.max(1, Math.floor(m.totalTeams * 0.25)),
    templates: [
      () => 'Top drafter. Top finisher. Congratulations on being annoyingly correct about everything.',
      () => 'Drafted well, managed well, won a lot. The league would like you to be worse at this.',
    ],
  },
  {
    id: 'late-round-fishing',
    matches: (m) => !!m.bestPickName && (m.bestPickRound ?? 0) >= 8,
    templates: [
      (m) =>
        `Found ${m.bestPickName} in round ${m.bestPickRound}. While everyone else was reaching, you were fishing.`,
      (m) => `${m.bestPickName} in round ${m.bestPickRound} was theft in broad daylight. No notes.`,
    ],
  },
  // Fallbacks by grade band so RoastComment never renders empty.
  {
    id: 'fallback-good',
    matches: (m) => !!m.draftGrade && gradeBand(m.draftGrade) === 'a',
    templates: [() => 'Honor roll. Insufferable about it, probably.'],
  },
  {
    id: 'fallback-mid',
    matches: (m) => !!m.draftGrade && gradeBand(m.draftGrade) === 'b',
    templates: [() => 'Solid. Unspectacular. The fantasy equivalent of a firm handshake.'],
  },
  {
    id: 'fallback-bad',
    matches: (m) =>
      !!m.draftGrade && (gradeBand(m.draftGrade) === 'c' || gradeBand(m.draftGrade) === 'f'),
    templates: [() => 'Shows potential. Does not show results.'],
  },
];

/**
 * Pick a deterministic roast for a team-season. Same seed → same roast,
 * across refreshes and shares.
 */
export function getRoastComment(metrics: RoastMetrics, teamId: number, season: number): string {
  const matching = RULES.filter((r) => r.matches(metrics));
  if (matching.length === 0) {
    return 'Not enough evidence to grade. Which, honestly, says plenty.';
  }
  const rand = mulberry32(teamId * 10007 + season);
  const rule = matching[Math.floor(rand() * matching.length)];
  const template = rule.templates[Math.floor(rand() * rule.templates.length)];
  return template(metrics);
}

/** Exposed for tests. */
export const __testables = { RULES, mulberry32 };
